import type {
  InvestmentQuoteTypeV2,
  InvestmentQuoteV2,
} from 'app-components/flux/investment-quotes-v2/investmentQuotesV2Types';
import { type assertInterface, tracker } from 'app-components/utils/core';
import type { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { createSecurity } from './securitiesActions';
import { type Security, type SecurityProps, SecurityTypeEnum, mkSecurity } from './securitiesTypes';

// securityIdType:securityId:symbol
export const securityKey = (security: SecurityProps | Security) =>
  `${security.securityIdType}:${security.securityId}:${security.symbol}`;

export const getSecurityTypeString = (securityType: string) => {
  switch (securityType) {
    case SecurityTypeEnum.STOCK:
      return 'Stock';
    case SecurityTypeEnum.MUTUALFUND:
      return 'Mutual fund';
    case SecurityTypeEnum.SWEEP:
      return 'Sweep';
    case SecurityTypeEnum.DEBT:
      return 'Debt';
    case SecurityTypeEnum.OPTION:
      return 'Option';
    case SecurityTypeEnum.BOND:
      return 'Bond';
    case SecurityTypeEnum.CRYPTOCURRENCY:
      return 'Cryptocurrency';
    case SecurityTypeEnum.OTHER:
      return 'Other';
    default:
      return 'Other';
  }
};

export const getPrice = (security: SecurityProps | Security | InvestmentQuoteV2 | InvestmentQuoteTypeV2) => {
  if (security && 'lastPrice' in security) {
    return security.lastPrice;
  } else if (security && 'currentUnitPrice' in security) {
    return security?.currentUnitPrice;
  }

  return undefined;
};

export const getUnknownSecurity = () =>
  mkSecurity({
    name: 'Unknown',
    symbol: 'Unknown',
    currentUnitPrice: 0,
  });

type Item = {
  name: string;
  symbol: string;
  security?: SecurityProps | Security | InvestmentQuoteV2 | InvestmentQuoteTypeV2;
  toJS?: () => Item;
};

function getSecurityIdType(security?: Item['security']) {
  if (!security || !(security && 'securityIdType' in security)) {
    return undefined;
  }

  return security.securityIdType;
}

function getSecurityId(security?: Item['security']) {
  if (!security || !(security && 'securityId' in security)) {
    return undefined;
  }

  return security.securityId;
}

// Create a security package for a new or existing security
// If a security is new and manually made, the item passed in will only have a name
// If a security is new and has a barchart value, the item passed in will also have a symbol (Along with other properties)
// If a security already exists, then we can just use the default values!
export const createNewSecurityPackage = (item: Item) => {
  const isNew = !item.security?.id && item.name;

  // Default values
  const securityIdType = getSecurityIdType(item.security);
  const clientId = item.security && 'clientId' in item.security ? item.security.clientId : undefined;
  const securityId = getSecurityId(item.security);

  return {
    ...('toJS' in item ? item.toJS?.() : item || {}),
    security: {
      ...(item.security && 'toJS' in item.security ? item.security.toJS() : item.security || {}),
      clientId: isNew ? uuidv4().toUpperCase() : clientId,
      securityId: securityId || item.symbol || uuidv4().toUpperCase(),
      securityIdType: securityIdType || (item.symbol ? 'QCSSYMBOL' : 'CLIENT_ID'), // Already exists OR create new one based on if barchart val exists
      securityType: 'STOCK', // Will be changeable in future
    },
    name: item.name,
    ...(item.symbol && { symbol: item.symbol }),
  };
};

// In this case, a security is ALWAYS new. We use this to create a brand new security, and then send the update package to investment transactions.
// If a security is new and manually made, it will only have a NAME
// If a security is new and has a barchart value, it will also have a symbol
export const createNewSecurityAndSendPackage = (
  item: Item,
  dispatch: Dispatch,
  assert: typeof assertInterface.assert,
  sendPackage: (item: Item, values: any) => void,
  callbackValues: any,
) => {
  tracker.track(tracker.events.addCustomSecurity, { name: item.name });
  new Promise((resolve: any, reject: any) => {
    dispatch(
      createSecurity(
        mkSecurity({
          clientId: uuidv4().toUpperCase(),
          securityId: item?.symbol || uuidv4().toUpperCase(),
          ...(item?.symbol && { securityIdType: 'QCSSYMBOL' }), // Default value is CLIENT_ID so we do not need to explicitly set it here
          name: item?.name,
          symbol: item?.symbol,
          securityType: 'STOCK', // Will be changeable in future
        }),
        { resolve, reject },
      ),
    );
  })
    .then((result: any) => {
      sendPackage(
        {
          ...('toJS' in item ? item.toJS?.() : item || {}),
          security: {
            ...(item.security && 'toJS' in item.security ? item.security.toJS() : item.security || {}),
            id: result?.data?.id || result.resource?.id || item.security?.id,
            securityIdType: result.resource?.securityIdType || getSecurityIdType(item.security),
            securityId: result.resource?.securityId || getSecurityId(item.security),
          } as SecurityProps,
          symbol: result.resource?.symbol || item.symbol,
          name: result.resource?.name || item.name,
        },
        callbackValues,
      );
    })
    .catch((error: any) => {
      assert(false, error);
      return {};
    });
};
